'use client';

import { memo, useState } from 'react';

import { Check, ChevronLeft, ChevronRight, Crown, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import type { AnimeCostume, CharacterConfig, Gender } from './types';
import { useSubscriptionStatus } from './use-subscription-status';

interface StepAnimeCostumeProps {
  config: CharacterConfig;
  updateConfig: (updates: Partial<CharacterConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}

const costumeOptions: { id: AnimeCostume; premium?: boolean }[] = [
  { id: 'school-uniform' },
  { id: 'maid' },
  { id: 'bikini' },
  { id: 'fantasy-armor' },
  { id: 'kimono' },
  { id: 'casual-anime' },
  { id: 'bunny-suit' },
  { id: 'nurse' },
  { id: 'naked', premium: true },
  { id: 'custom', premium: true },
];

function getCostumeImage(id: string, gender: Gender) {
  return `/images/character-creator/card-images/5th-step/anime-costume/${gender}/${id}/1.avif`;
}

export const StepAnimeCostume = memo(function StepAnimeCostume({
  config,
  updateConfig,
  onBack,
  onNext,
}: StepAnimeCostumeProps) {
  const { t } = useTranslation('common');
  const { isSubscribed } = useSubscriptionStatus();
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const handleSelect = (id: AnimeCostume, premium?: boolean) => {
    if (premium && !isSubscribed) return;
    updateConfig({ animeCostume: id });
    if (id !== 'custom') {
      updateConfig({ animeCostumeCustom: undefined });
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-2 sm:px-4 lg:px-0">
      <div className="mb-3 text-center sm:mb-4">
        <h1 className="mb-1 text-xl font-bold text-white sm:text-2xl md:text-3xl">
          <Trans i18nKey="common:characterCreator.chooseCostume" />
        </h1>
        <p className="text-xs text-gray-400 sm:text-sm">
          <Trans i18nKey="common:characterCreator.pickAnimeCostume" />
        </p>
      </div>

      <div className="mx-auto grid max-w-[360px] grid-cols-5 gap-1.5 sm:max-w-lg sm:gap-2 md:max-w-2xl md:gap-3 lg:max-w-4xl">
        {costumeOptions.map((option) => {
          const isSelected = config.animeCostume === option.id;
          const isLocked = option.premium && !isSubscribed;
          const hasImgError = imgErrors[option.id];
          const label = t(`characterCreator.options.animeCostume.${option.id}`);

          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id, option.premium)}
              className={`relative aspect-[3/4] overflow-hidden rounded-xl transition-all duration-300 ${
                isSelected
                  ? 'border-2 border-pink-500 shadow-[0_0_14px_rgba(236,72,153,0.5)]'
                  : isLocked
                    ? 'border border-white/10 opacity-70'
                    : 'border border-white/10 hover:border-white/30'
              }`}
            >
              {option.id !== 'custom' && !hasImgError ? (
                <img
                  src={getCostumeImage(option.id, config.gender)}
                  alt={label}
                  className="h-full w-full object-cover"
                  onError={() =>
                    setImgErrors((prev) => ({ ...prev, [option.id]: true }))
                  }
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  {option.premium ? (
                    <Crown className="h-8 w-8 text-amber-400 sm:h-10 sm:w-10" />
                  ) : (
                    <span className="text-xs font-medium text-gray-400 sm:text-sm">
                      {label}
                    </span>
                  )}
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
              <div className="absolute right-0 bottom-0 left-0 p-1.5 sm:p-2">
                <h3 className="text-center text-xs font-bold text-white sm:text-sm">
                  {label}
                </h3>
              </div>
              {isSelected && !isLocked && (
                <div className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 shadow-lg sm:h-5 sm:w-5">
                  <Check className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
                </div>
              )}
              {isLocked && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
                  <Lock className="mb-1.5 h-5 w-5 text-amber-400 sm:h-6 sm:w-6" />
                  <span className="text-xs font-semibold text-amber-300">
                    <Trans i18nKey="common:characterCreator.premiumLabel" />
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {config.animeCostume === 'custom' && isSubscribed && (
        <div className="mx-auto mt-3 max-w-md sm:mt-4">
          <input
            type="text"
            value={config.animeCostumeCustom || ''}
            onChange={(e) =>
              updateConfig({ animeCostumeCustom: e.target.value })
            }
            placeholder={t('characterCreator.costumeCustomPlaceholder')}
            className="w-full rounded-xl border border-white/20 bg-black/50 px-3 py-2.5 text-sm text-white placeholder-gray-500 transition-all focus:border-pink-500/60 focus:ring-1 focus:ring-pink-500/30 focus:outline-none sm:px-4 sm:py-3"
          />
        </div>
      )}

      <div className="mt-4 flex justify-center gap-2.5 sm:mt-5 sm:gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 rounded-xl border-2 border-white/20 bg-transparent px-5 py-2.5 text-xs font-semibold text-white transition-all hover:border-white/40 hover:bg-white/5 sm:px-6 sm:py-3 sm:text-sm"
        >
          <ChevronLeft className="h-4 w-4" />
          <Trans i18nKey="common:characterCreator.backButton" />
        </button>
        <button
          onClick={onNext}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-red-600 via-pink-600 to-pink-700 px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-pink-500/30 transition-all hover:from-red-700 hover:via-pink-700 hover:to-pink-800 sm:flex-none sm:px-10 sm:py-3 sm:text-sm"
        >
          <Trans i18nKey="common:characterCreator.nextStep" />
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});
